import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { Snapshot } from "../api";
import type { NavGroupId } from "../features/types";
import { buildMenu, groupOf, loadOpenGroups, navBadges, saveOpenGroups, type NavGroup, type NavPage } from "./menu";

/**
 * The grouped side menu (also the phone drawer's contents). Groups with one
 * page show as a plain link; the others fold open. The group holding the page
 * on screen opens by itself; which other groups are open is remembered per
 * person in this browser. Folded groups render no items, so keyboard focus
 * never lands on something hidden.
 */
export function SideNav({ s, page, go, menu: given }: { s: Snapshot; page: string; go: (page: string) => void; menu?: NavGroup[] }) {
  const menu = useMemo(() => given ?? buildMenu(s), [given, s]);
  const badges = navBadges(s);
  const userId = String(s.user?.id ?? "");
  const current = groupOf(menu, page);
  const [open, setOpen] = useState<Set<NavGroupId>>(() => new Set([...loadOpenGroups(userId), ...(current ? [current] : [])]));
  // Another person signed in on this device: their own open groups.
  const lastUser = useRef(userId);
  useEffect(() => {
    if (lastUser.current === userId) return;
    lastUser.current = userId;
    setOpen(new Set([...loadOpenGroups(userId), ...(current ? [current] : [])]));
  }, [userId]);
  // Opening a page opens its group.
  useEffect(() => {
    if (current) setOpen((o) => (o.has(current) ? o : new Set(o).add(current)));
  }, [current]);
  // Keep the page on screen visible inside the scrolling menu.
  const active = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    active.current?.scrollIntoView?.({ block: "nearest" });
  }, [page, open]);
  const toggle = (id: NavGroupId) =>
    setOpen((o) => {
      const next = new Set(o);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      saveOpenGroups(userId, [...next]);
      return next;
    });
  const badge = (b: { count: number; text: string } | undefined) =>
    b ? (
      <span className="nav-badge">
        <span aria-hidden="true">{b.count > 99 ? "99+" : b.count}</span>
        <span className="nav-sr">, {b.text}</span>
      </span>
    ) : null;
  const link = (p: NavPage, top: boolean) => (
    <button
      ref={page === p.id ? active : undefined}
      className={`nav-item${top ? " nav-top" : ""}${page === p.id ? " active" : ""}`}
      aria-current={page === p.id ? "page" : undefined}
      data-page={p.id}
      onClick={() => go(p.id)}
    >
      {top && <p.icon size={18} />}
      <span className="nav-label">{p.label}</span>
      {badge(badges[p.id])}
    </button>
  );
  return (
    <nav className="side-nav" aria-label="Main menu">
      <ul>
        {menu.map((g) => {
          if (g.items.length === 1) return <li key={g.id}>{link(g.items[0], true)}</li>;
          const isOpen = open.has(g.id);
          const pendingCount = g.items.reduce((n, p) => n + (badges[p.id]?.count ?? 0), 0);
          return (
            <li key={g.id} className={`nav-section${isOpen ? " open" : ""}`}>
              <button
                className={`nav-group${current === g.id ? " current" : ""}`}
                aria-expanded={isOpen}
                aria-controls={`nav-group-${g.id}`}
                data-group={g.id}
                onClick={() => toggle(g.id)}
              >
                <g.icon size={18} />
                <span className="nav-label">{g.label}</span>
                {!isOpen && badge(pendingCount ? { count: pendingCount, text: "need attention" } : undefined)}
                <ChevronDown size={15} className="nav-chevron" aria-hidden="true" />
              </button>
              {isOpen && (
                <ul id={`nav-group-${g.id}`} className="nav-items">
                  {g.items.map((p) => (
                    <li key={p.id}>{link(p, false)}</li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
